import { useEffect, useMemo, useState } from "react";
import api, { fmtBRL, fmtInt, fmtNum, fmtPct } from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar,
} from "recharts";
import { ArrowUp, ArrowDown, RefreshCw, Minus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// -------------------- Date helpers --------------------
function iso(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function startOfWeek(d) { const c = new Date(d); const day = (c.getDay() + 6) % 7; c.setDate(c.getDate() - day); return c; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }

const PRESETS = [
  {
    id: "today", label: "Hoje",
    compute: () => { const t = new Date(); const y = addDays(t, -1); return { a: { s: iso(t), e: iso(t) }, b: { s: iso(y), e: iso(y) } }; },
  },
  {
    id: "yesterday", label: "Ontem",
    compute: () => { const y = addDays(new Date(), -1); const dby = addDays(y, -1); return { a: { s: iso(y), e: iso(y) }, b: { s: iso(dby), e: iso(dby) } }; },
  },
  {
    id: "last7", label: "Últimos 7 dias",
    compute: () => { const t = new Date(); const e = addDays(t, -1); const s = addDays(e, -6); const pe = addDays(s, -1); const ps = addDays(pe, -6); return { a: { s: iso(s), e: iso(e) }, b: { s: iso(ps), e: iso(pe) } }; },
  },
  {
    id: "last30", label: "Últimos 30 dias",
    compute: () => { const t = new Date(); const e = addDays(t, -1); const s = addDays(e, -29); const pe = addDays(s, -1); const ps = addDays(pe, -29); return { a: { s: iso(s), e: iso(e) }, b: { s: iso(ps), e: iso(pe) } }; },
  },
  {
    id: "week", label: "Esta semana",
    compute: () => { const ws = startOfWeek(new Date()); const we = addDays(ws, 6); const ps = addDays(ws, -7); const pe = addDays(ws, -1); return { a: { s: iso(ws), e: iso(we) }, b: { s: iso(ps), e: iso(pe) } }; },
  },
  {
    id: "month", label: "Este mês",
    compute: () => { const t = new Date(); const ms = startOfMonth(t); const me = new Date(t.getFullYear(), t.getMonth() + 1, 0); const ps = new Date(t.getFullYear(), t.getMonth() - 1, 1); const pe = new Date(t.getFullYear(), t.getMonth(), 0); return { a: { s: iso(ms), e: iso(me) }, b: { s: iso(ps), e: iso(pe) } }; },
  },
  { id: "custom", label: "Personalizado", compute: null },
];

const INITIAL_PRESET_ID = "last7";
const INITIAL_PERIODS = (PRESETS.find(p => p.id === INITIAL_PRESET_ID)).compute();

// -------------------- Components --------------------
function Delta({ pct, inverted = false }) {
  if (pct === null || pct === undefined || !isFinite(pct)) {
    return <span className="font-display text-xs" style={{ color: "var(--ifl-muted)" }}>—</span>;
  }
  const up = pct >= 0;
  const positive = inverted ? !up : up;
  const color = pct === 0 ? "var(--ifl-muted)" : positive ? "var(--ifl-success)" : "var(--ifl-danger)";
  const Icon = pct === 0 ? Minus : up ? ArrowUp : ArrowDown;
  return (
    <span className="font-display text-xs inline-flex items-center gap-1" style={{ color }}>
      <Icon size={12} strokeWidth={2.5} />
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function KPI({ label, value, hint, delta, accent, testId }) {
  return (
    <div className="ifl-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="label-caps">{label}</div>
        {delta !== undefined && <Delta pct={delta} />}
      </div>
      <div className="font-display text-3xl tabular-nums" style={{ color: accent || "var(--ifl-text)", letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {hint && <div className="font-body text-xs mt-2" style={{ color: "var(--ifl-muted)" }}>{hint}</div>}
    </div>
  );
}

function FilterPill({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="px-3 py-1.5 rounded-full border font-display uppercase tracking-wider text-xs transition-all"
      style={{
        background: active ? "var(--ifl-text)" : "transparent",
        color: active ? "#fff" : "var(--ifl-text)",
        borderColor: active ? "var(--ifl-text)" : "var(--ifl-border)",
      }}
    >
      {children}
    </button>
  );
}

// -------------------- Main --------------------
export default function Sales() {
  const [presetId, setPresetId] = useState(INITIAL_PRESET_ID);
  const [a, setA] = useState(INITIAL_PERIODS.a);
  const [b, setB] = useState(INITIAL_PERIODS.b);
  const [compareOn, setCompareOn] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [gaps, setGaps] = useState(null);
  const [fillingGaps, setFillingGaps] = useState(false);

  const applyPreset = (id) => {
    setPresetId(id);
    const p = PRESETS.find(x => x.id === id);
    if (p && p.compute) {
      const { a: pa, b: pb } = p.compute();
      setA(pa); setB(pb);
    }
  };

  const fetchData = async (pa = a, pb = b, withCompare = compareOn) => {
    if (!pa.s || !pa.e) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: pa.s, end_date: pa.e });
      if (withCompare && pb.s && pb.e) {
        params.set("compare_start", pb.s);
        params.set("compare_end", pb.e);
      }
      const r = await api.get(`/reports/sales?${params}`);
      setData(r.data);
      // Detect gaps in the current period (days with zero cupons)
      const daysInPeriod = (() => {
        const s = new Date(pa.s), e = new Date(pa.e);
        return Math.round((e - s) / 86400000) + 1;
      })();
      try {
        const g = await api.get(`/sync/pdv/gaps?days=${Math.min(daysInPeriod, 60)}`);
        // Filter to only gaps WITHIN the selected period
        const inPeriod = (g.data.missing_dates || []).filter(d => d >= pa.s && d <= pa.e);
        setGaps({ ...g.data, missing_dates_in_period: inPeriod });
      } catch (_) { setGaps(null); }
    } catch (e) {
      toast.error("Falha ao carregar vendas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(a, b, compareOn); /* eslint-disable-next-line */ }, [a.s, a.e, b.s, b.e, compareOn]);

  const refresh = async () => {
    setRefreshing(true);
    const t = toast.loading(`Sincronizando vendas de ${a.s} a ${a.e}…`);
    try {
      const r = await api.post("/sync/pdv/sales", { start_date: a.s, end_date: a.e });
      const ins = r.data?.inserted ?? 0;
      const upd = r.data?.updated ?? 0;
      toast.success(`Vendas atualizadas: ${ins} novo(s), ${upd} atualizados`, { id: t });
      fetchData();
    } catch (e) {
      toast.error("Falha ao atualizar vendas", { id: t });
    } finally {
      setRefreshing(false);
    }
  };

  const fillGaps = async () => {
    setFillingGaps(true);
    const t = toast.loading("Procurando e preenchendo dias sem dados…");
    try {
      const r = await api.post("/sync/pdv/fill-gaps?days=60");
      const filled = r.data?.filled ?? 0;
      const ins = r.data?.inserted ?? 0;
      if (filled === 0) toast.success("Nenhum dia faltando — sua base está completa", { id: t });
      else toast.success(`${filled} dia(s) preenchido(s) · ${ins} cupons recuperados`, { id: t });
      fetchData();
    } catch (e) {
      toast.error("Falha ao preencher dias faltantes", { id: t });
    } finally {
      setFillingGaps(false);
    }
  };

  // -------------------- Derived --------------------
  const cur = data?.current;
  const prev = data?.previous;
  const dlt = data?.deltas;

  // Merge daily series for comparison chart
  const chartData = useMemo(() => {
    if (!cur?.daily) return [];
    const aDaily = cur.daily;
    const bDaily = compareOn ? (prev?.daily || []) : [];
    const len = Math.max(aDaily.length, bDaily.length);
    const rows = [];
    for (let i = 0; i < len; i++) {
      const r = {};
      if (i < aDaily.length) {
        r.date = aDaily[i].date;
        r.weekday = aDaily[i].weekday;
        r.cur_revenue = aDaily[i].revenue;
        r.cur_tickets = aDaily[i].tickets;
        r.label = aDaily[i].date.slice(5);
      }
      if (i < bDaily.length) {
        r.prev_date = bDaily[i].date;
        r.prev_revenue = bDaily[i].revenue;
        if (!r.label) r.label = bDaily[i].date.slice(5);
      }
      rows.push(r);
    }
    return rows;
  }, [cur, prev, compareOn]);

  // Best/worst day for highlights
  const bestWorst = useMemo(() => {
    if (!cur?.daily?.length) return { best: null, worst: null };
    const sorted = [...cur.daily].sort((x, y) => y.revenue - x.revenue);
    return { best: sorted[0], worst: sorted[sorted.length - 1] };
  }, [cur]);

  const totalRows = cur?.daily?.length || 0;

  const downloadCSV = () => {
    if (!cur?.daily?.length) return;
    const header = ["Data", "Dia", "Receita", "Cupons", "Ticket médio"];
    const lines = [header.join(",")];
    cur.daily.forEach(d => {
      lines.push([d.date, d.weekday, d.revenue, d.tickets, d.avg_ticket].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `vendas_${a.s}_${a.e}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="sales-page">
      <PageHeader
        eyebrow="Operações · Vendas"
        title="O DIA A DIA"
        accent="das vendas."
        description="Visão operacional do faturamento por dia, dia da semana, horário e formas de pagamento."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadCSV} className="ifl-btn-outline" data-testid="export-csv-btn" disabled={!cur?.daily?.length}>
              Exportar CSV
            </button>
            <button onClick={refresh} className="ifl-btn" disabled={refreshing} data-testid="refresh-sales">
              <RefreshCw size={14} strokeWidth={2} className={refreshing ? "ifl-pulse" : ""} />
              {refreshing ? "Atualizando…" : "Atualizar vendas"}
            </button>
          </div>
        }
        testId="sales-header"
      />

      {/* ============ FILTERS ============ */}
      <div className="ifl-card p-5 mb-6" data-testid="filters-card">
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <div className="label-caps mr-2">Período</div>
          {PRESETS.map(p => (
            <FilterPill key={p.id} active={presetId === p.id} onClick={() => applyPreset(p.id)} testId={`preset-${p.id}`}>
              {p.label}
            </FilterPill>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-end">
          <div>
            <div className="label-caps mb-1">De</div>
            <input
              type="date"
              value={a.s}
              onChange={(e) => { setA({ ...a, s: e.target.value }); setPresetId("custom"); }}
              className="ifl-input w-full"
              data-testid="period-a-start"
            />
          </div>
          <div>
            <div className="label-caps mb-1">Até</div>
            <input
              type="date"
              value={a.e}
              onChange={(e) => { setA({ ...a, e: e.target.value }); setPresetId("custom"); }}
              className="ifl-input w-full"
              data-testid="period-a-end"
            />
          </div>
          <div className="lg:col-span-1 flex items-center gap-2 pb-1">
            <input
              id="cmpOn"
              type="checkbox"
              checked={compareOn}
              onChange={(e) => setCompareOn(e.target.checked)}
              className="w-4 h-4"
              data-testid="compare-toggle"
            />
            <label htmlFor="cmpOn" className="label-caps cursor-pointer select-none">Comparar com</label>
          </div>
          <div>
            <div className="label-caps mb-1">Comparação · De</div>
            <input
              type="date"
              value={b.s}
              disabled={!compareOn}
              onChange={(e) => { setB({ ...b, s: e.target.value }); setPresetId("custom"); }}
              className="ifl-input w-full"
              data-testid="period-b-start"
            />
          </div>
          <div>
            <div className="label-caps mb-1">Comparação · Até</div>
            <input
              type="date"
              value={b.e}
              disabled={!compareOn}
              onChange={(e) => { setB({ ...b, e: e.target.value }); setPresetId("custom"); }}
              className="ifl-input w-full"
              data-testid="period-b-end"
            />
          </div>
        </div>
      </div>

      {loading && !data && <div className="font-body" style={{ color: "var(--ifl-muted)" }} data-testid="sales-loading">Carregando…</div>}

      {/* Gap alert — show when there are days without data in the selected period */}
      {gaps && gaps.missing_dates_in_period && gaps.missing_dates_in_period.length > 0 && (
        <div
          className="ifl-card p-5 mb-6 flex items-start gap-4 flex-wrap"
          style={{ background: "#FCEFEA", borderColor: "var(--ifl-terracotta)" }}
          data-testid="gap-alert"
        >
          <AlertTriangle size={20} strokeWidth={1.6} style={{ color: "var(--ifl-terracotta)", flexShrink: 0, marginTop: 2 }} />
          <div className="flex-1 min-w-0">
            <div className="label-caps" style={{ color: "var(--ifl-terracotta)" }}>Dias sem dados no período</div>
            <div className="font-body text-sm mt-1" style={{ color: "var(--ifl-text)" }}>
              Detectamos <strong>{gaps.missing_dates_in_period.length} dia(s)</strong> sem cupons:{" "}
              <span className="font-mono text-xs">
                {gaps.missing_dates_in_period.slice(0, 10).map(d => d.slice(5).replace("-", "/")).join(", ")}
                {gaps.missing_dates_in_period.length > 10 && ` +${gaps.missing_dates_in_period.length - 10}`}
              </span>
              . Isso normalmente acontece quando a sincronização horária pulou esses dias. Clique abaixo pra buscar e preencher direto do PDV.
            </div>
          </div>
          <button
            onClick={fillGaps}
            disabled={fillingGaps}
            className="ifl-btn shrink-0"
            data-testid="fill-gaps-btn"
          >
            <RefreshCw size={14} strokeWidth={2} className={fillingGaps ? "ifl-pulse" : ""} />
            {fillingGaps ? "Preenchendo…" : "Preencher dias faltantes"}
          </button>
        </div>
      )}

      {cur && (
        <>
          {/* ============ KPIs ============ */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <KPI
              testId="kpi-revenue"
              label="Receita PDV"
              value={fmtBRL(cur.revenue)}
              hint={`${fmtBRL(cur.daily_avg_revenue)}/dia · ${cur.period.days}d`}
              delta={dlt?.revenue?.pct}
              accent="var(--ifl-text)"
            />
            <KPI
              testId="kpi-events"
              label="Eventos (Omie)"
              value={fmtBRL(cur.events_revenue || 0)}
              hint={`${cur.events_count || 0} OS no período`}
              accent="var(--ifl-mustard)"
            />
            <KPI
              testId="kpi-total-revenue"
              label="Faturamento Total"
              value={fmtBRL(cur.total_revenue || cur.revenue)}
              hint="PDV + Eventos"
              accent="var(--ifl-success)"
            />
            <KPI
              testId="kpi-tickets"
              label="Cupons"
              value={fmtInt(cur.tickets)}
              hint={`${fmtNum(cur.daily_avg_tickets, 1)}/dia`}
              delta={dlt?.tickets?.pct}
            />
            <KPI
              testId="kpi-avg-ticket"
              label="Ticket médio"
              value={fmtBRL(cur.avg_ticket)}
              hint={`${fmtNum(cur.avg_items_per_ticket, 1)} itens/cupom`}
              delta={dlt?.avg_ticket?.pct}
              accent="var(--ifl-terracotta)"
            />
          </div>

          {/* ============ RECONCILIATION CARD ============ */}
          <div className="ifl-card p-6 mb-6" data-testid="reconciliation-card">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="label-caps">Reconciliação</div>
                <div className="font-italic-serif text-xl mt-1">PDV × Omie no período.</div>
              </div>
              <div className="font-body text-xs" style={{ color: "var(--ifl-muted)" }}>
                Confere se os totais batem entre os dois sistemas.
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl border" style={{ borderColor: "var(--ifl-border)", background: "rgba(15,27,45,0.03)" }}>
                <div className="label-caps mb-1">PDV (cupons)</div>
                <div className="font-display text-2xl tabular-nums" style={{ color: "var(--ifl-text)" }}>{fmtBRL(cur.revenue)}</div>
                <div className="font-body text-xs mt-1" style={{ color: "var(--ifl-muted)" }}>{fmtInt(cur.tickets)} cupons</div>
              </div>
              <div className="p-4 rounded-xl border" style={{ borderColor: "var(--ifl-mustard)", background: "rgba(196,138,46,0.06)" }}>
                <div className="label-caps mb-1" style={{ color: "var(--ifl-mustard)" }}>Eventos (OS)</div>
                <div className="font-display text-2xl tabular-nums" style={{ color: "var(--ifl-mustard)" }}>{fmtBRL(cur.events_revenue || 0)}</div>
                <div className="font-body text-xs mt-1" style={{ color: "var(--ifl-muted)" }}>{cur.events_count || 0} ordens</div>
              </div>
              <div className="p-4 rounded-xl border" style={{ borderColor: "var(--ifl-success)", background: "rgba(79,124,83,0.06)" }}>
                <div className="label-caps mb-1" style={{ color: "var(--ifl-success)" }}>Faturamento total</div>
                <div className="font-display text-2xl tabular-nums" style={{ color: "var(--ifl-success)" }}>{fmtBRL(cur.total_revenue || cur.revenue)}</div>
                <div className="font-body text-xs mt-1" style={{ color: "var(--ifl-muted)" }}>PDV + Eventos</div>
              </div>
              <div className="p-4 rounded-xl border" style={{ borderColor: "var(--ifl-border)" }}>
                <div className="label-caps mb-1">Participação eventos</div>
                <div className="font-display text-2xl tabular-nums" style={{ color: "var(--ifl-text)" }}>
                  {cur.total_revenue ? fmtNum((cur.events_revenue || 0) / cur.total_revenue * 100, 1) : "0,0"}%
                </div>
                <div className="font-body text-xs mt-1" style={{ color: "var(--ifl-muted)" }}>do faturamento total</div>
              </div>
            </div>
          </div>

          {/* ============ MAIN CHART ============ */}
          <div className="ifl-card p-6 mb-6" data-testid="daily-chart-card">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="label-caps">Faturamento diário</div>
                <div className="font-italic-serif text-xl mt-1">como cada dia performa.</div>
              </div>
              <div className="font-body text-xs" style={{ color: "var(--ifl-muted)" }}>
                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: "var(--ifl-text)" }}></span>
                Período atual ({a.s} → {a.e})
                {compareOn && prev && (
                  <>
                    <span className="inline-block w-2 h-2 rounded-full mr-2 ml-4" style={{ background: "var(--ifl-mustard)", border: "1px dashed var(--ifl-mustard)" }}></span>
                    Comparação ({b.s} → {b.e})
                  </>
                )}
              </div>
            </div>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#6B6E76", fontSize: 11, fontFamily: "DM Mono" }} tickLine={false} axisLine={{ stroke: "#E5E1D8" }} />
                  <YAxis tick={{ fill: "#6B6E76", fontSize: 11, fontFamily: "DM Mono" }} tickLine={false} axisLine={{ stroke: "#E5E1D8" }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #E5E1D8", borderRadius: 8, fontFamily: "DM Sans, sans-serif" }}
                    formatter={(v, name) => [fmtBRL(v), name === "cur_revenue" ? "Atual" : "Comparação"]}
                    labelFormatter={(label, payload) => {
                      const row = payload?.[0]?.payload;
                      if (!row) return label;
                      return `${row.weekday || ""} · ${row.date || label}`;
                    }}
                  />
                  <Line type="monotone" dataKey="cur_revenue" stroke="#0F1B2D" strokeWidth={2.5} dot={{ r: 3, fill: "#0F1B2D" }} activeDot={{ r: 5 }} name="cur_revenue" />
                  {compareOn && prev && (
                    <Line type="monotone" dataKey="prev_revenue" stroke="#C48A2E" strokeDasharray="5 4" strokeWidth={2} dot={{ r: 2, fill: "#C48A2E" }} name="prev_revenue" />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ============ SECONDARY GRID ============ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Weekday */}
            <div className="ifl-card p-6" data-testid="weekday-card">
              <div className="label-caps">Dia da semana</div>
              <div className="font-italic-serif text-lg mb-3">média por dia.</div>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={cur.weekday} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "#6B6E76", fontSize: 11, fontFamily: "DM Mono" }} tickLine={false} axisLine={{ stroke: "#E5E1D8" }} />
                    <YAxis tick={{ fill: "#6B6E76", fontSize: 10, fontFamily: "DM Mono" }} tickLine={false} axisLine={{ stroke: "#E5E1D8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #E5E1D8", borderRadius: 8, fontFamily: "DM Sans, sans-serif" }}
                      formatter={(v) => fmtBRL(v)}
                      labelFormatter={(label, p) => `${label} · média de ${p?.[0]?.payload?.occurrences || 0} dia(s)`}
                    />
                    <Bar dataKey="avg_revenue" fill="#B8533B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Hourly */}
            <div className="ifl-card p-6" data-testid="hourly-card">
              <div className="label-caps">Horário de pico</div>
              <div className="font-italic-serif text-lg mb-3">cupons por hora.</div>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={cur.hourly} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "#6B6E76", fontSize: 9, fontFamily: "DM Mono" }} tickLine={false} axisLine={{ stroke: "#E5E1D8" }} interval={1} />
                    <YAxis tick={{ fill: "#6B6E76", fontSize: 10, fontFamily: "DM Mono" }} tickLine={false} axisLine={{ stroke: "#E5E1D8" }} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #E5E1D8", borderRadius: 8, fontFamily: "DM Sans, sans-serif" }}
                      formatter={(v, n) => n === "tickets" ? [v, "Cupons"] : [fmtBRL(v), "Receita"]}
                    />
                    <Bar dataKey="tickets" fill="#0F1B2D" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Payments */}
            <div className="ifl-card p-6" data-testid="payments-card">
              <div className="label-caps">Formas de pagamento</div>
              <div className="font-italic-serif text-lg mb-3">como entram.</div>
              <div className="flex flex-col gap-3 mt-2">
                {(cur.payments || []).slice(0, 6).map((p) => {
                  const max = Math.max(...(cur.payments || []).map(x => x.value));
                  const w = max ? Math.max(4, (p.value / max) * 100) : 0;
                  return (
                    <div key={p.name}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-body" style={{ color: "var(--ifl-text)" }}>{p.name}</span>
                        <span className="font-display tabular-nums" style={{ color: "var(--ifl-muted)" }}>{fmtBRL(p.value)}</span>
                      </div>
                      <div className="h-2 rounded-full" style={{ background: "var(--ifl-border)" }}>
                        <div style={{ width: `${w}%`, height: "100%", background: "var(--ifl-terracotta)", borderRadius: 999, transition: "width 400ms ease-out" }} />
                      </div>
                    </div>
                  );
                })}
                {(!cur.payments || cur.payments.length === 0) && (
                  <div className="font-body text-sm" style={{ color: "var(--ifl-muted)" }}>Sem dados no período.</div>
                )}
              </div>
            </div>
          </div>

          {/* ============ DAILY TABLE ============ */}
          <div className="ifl-card overflow-hidden mb-6" data-testid="daily-table-card">
            <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-2 border-b" style={{ borderColor: "var(--ifl-border)" }}>
              <div>
                <div className="label-caps">Detalhe diário</div>
                <div className="font-italic-serif text-lg mt-1">linha por dia.</div>
              </div>
              <div className="font-display text-xs uppercase tracking-widest" style={{ color: "var(--ifl-muted)" }}>{totalRows} dia(s)</div>
            </div>
            <div className="overflow-x-auto">
              <table className="ifl-table" data-testid="daily-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Dia</th>
                    <th className="text-right">Receita</th>
                    <th className="text-right">Cupons</th>
                    <th className="text-right">Ticket médio</th>
                    {compareOn && prev && <th className="text-right">vs. comparação</th>}
                  </tr>
                </thead>
                <tbody>
                  {(cur.daily || []).map((d, idx) => {
                    const prevDay = compareOn && prev?.daily?.[idx];
                    const diffPct = prevDay && prevDay.revenue ? ((d.revenue - prevDay.revenue) / prevDay.revenue) * 100 : null;
                    const isBest = bestWorst.best?.date === d.date;
                    const isWorst = bestWorst.worst?.date === d.date;
                    return (
                      <tr key={d.date} style={{ background: isBest ? "rgba(79,124,83,0.06)" : isWorst ? "rgba(196,76,46,0.04)" : "transparent" }}>
                        <td className="font-mono text-sm">{d.date.slice(5).replace("-", "/")}<span className="ml-2 font-display text-xs" style={{ color: "var(--ifl-muted)" }}>{d.date.slice(0, 4)}</span></td>
                        <td className="font-display text-xs uppercase tracking-wider" style={{ color: isBest ? "var(--ifl-success)" : isWorst ? "var(--ifl-danger)" : "var(--ifl-muted)" }}>{d.weekday}{isBest ? " · melhor" : isWorst ? " · pior" : ""}</td>
                        <td className="text-right font-display tabular-nums" style={{ color: "var(--ifl-text)" }}>{fmtBRL(d.revenue)}</td>
                        <td className="text-right font-display tabular-nums">{fmtInt(d.tickets)}</td>
                        <td className="text-right font-display tabular-nums" style={{ color: "var(--ifl-muted)" }}>{fmtBRL(d.avg_ticket)}</td>
                        {compareOn && prev && (
                          <td className="text-right">
                            <Delta pct={diffPct} />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {(!cur.daily || cur.daily.length === 0) && (
                    <tr><td colSpan={compareOn ? 6 : 5} className="text-center py-12" style={{ color: "var(--ifl-muted)" }}>Sem vendas no período selecionado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ============ TOP DISHES ============ */}
          {cur.top_items?.length > 0 && (
            <div className="ifl-card p-6 mb-6" data-testid="top-items-card">
              <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                <div>
                  <div className="label-caps">Top pratos</div>
                  <div className="font-italic-serif text-xl mt-1">o que mais saiu.</div>
                </div>
                <div className="font-display text-xs uppercase tracking-widest" style={{ color: "var(--ifl-muted)" }}>Por receita</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {cur.top_items.slice(0, 10).map((it, i) => {
                  const max = cur.top_items[0].value;
                  const w = max ? (it.value / max) * 100 : 0;
                  return (
                    <div key={`${it.code}-${i}`} className="flex flex-col gap-1.5">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="flex items-baseline gap-2 min-w-0">
                          <span className="font-display text-xs" style={{ color: "var(--ifl-muted)" }}>#{i + 1}</span>
                          <span className="font-body text-sm truncate" style={{ color: "var(--ifl-text)" }}>{it.name || it.code}</span>
                        </div>
                        <div className="font-display tabular-nums text-sm shrink-0" style={{ color: "var(--ifl-text)" }}>{fmtBRL(it.value)}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-1.5 rounded-full" style={{ background: "var(--ifl-border)" }}>
                          <div style={{ width: `${w}%`, height: "100%", background: "var(--ifl-text)", borderRadius: 999 }} />
                        </div>
                        <div className="font-display text-xs tabular-nums shrink-0" style={{ color: "var(--ifl-muted)" }}>{fmtInt(it.qty)} un</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
